
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { translations, Language, TranslationKeys } from '@/translations/phemex';

type PhemexLangContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: TranslationKeys;
};

const defaultLanguage: Language = 'en';

// Create context with default values
const PhemexLangContext = createContext<PhemexLangContextType>({
  lang: defaultLanguage,
  setLang: () => {},
  t: translations[defaultLanguage],
});

export const usePhemexLang = () => useContext(PhemexLangContext);

interface PhemexLangProviderProps {
  children: ReactNode;
}

export const PhemexLangProvider = ({ children }: PhemexLangProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState<Language>(defaultLanguage);

  // Initialize language from URL parameter or localStorage
  useEffect(() => {
    const urlLang = searchParams.get('lang') as Language;
    const savedLang = localStorage.getItem('phemexPrefLang') as Language;
    
    if (urlLang && translations[urlLang]) {
      setLangState(urlLang);
      localStorage.setItem('phemexPrefLang', urlLang);
    } else if (savedLang && translations[savedLang]) {
      setLangState(savedLang);
    }
  }, [searchParams]);

  // Update language and save to localStorage
  const setLang = (newLang: Language) => {
    if (translations[newLang]) {
      setLangState(newLang);
      localStorage.setItem('phemexPrefLang', newLang);
      
      // Update URL parameter
      searchParams.set('lang', newLang);
      setSearchParams(searchParams);
    }
  };

  // Get current translations
  const t = translations[lang];

  return (
    <PhemexLangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </PhemexLangContext.Provider>
  );
};
